import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import { useReportCategories } from "../../hooks/useReportCategories";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type InstructorPerformanceResponse,
  type InstructorPerformanceItem,
  type InstructorPerformanceParams,
} from "../../services/reports-extended.api";
import { EmployeeManagement, Heart, Approve } from "@/components/icons";

const PAGE_SIZE = 20;

const TrainerPerformanceReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const categories = useReportCategories();

  const params: InstructorPerformanceParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
      categoryIds: filter.categoryIds,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    InstructorPerformanceResponse,
    InstructorPerformanceParams
  >({
    fetcher: (p, signal) =>
      reportsExtendedApi.getInstructorPerformance(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const topByScore = [...items]
    .filter((i) => i.avgExamScore > 0)
    .sort((a, b) => b.avgExamScore - a.avgExamScore)
    .slice(0, 10);

  const columns: TableColumn<InstructorPerformanceItem>[] = [
    {
      key: "fullName",
      label: t("reports.trainerPerformance.headerTrainer"),
      header: t("reports.trainerPerformance.headerTrainer"),
      accessor: (row) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{row.fullName}</span>
          {row.email && (
            <span style={{ fontSize: 12, color: "#64748b" }}>{row.email}</span>
          )}
        </div>
      ),
    },
    {
      key: "educationCount",
      label: t("reports.trainerPerformance.headerTraining"),
      header: t("reports.trainerPerformance.headerTraining"),
      accessor: (row) => row.educationCount,
    },
    {
      key: "totalParticipants",
      label: "KATILIMCI",
      header: "KATILIMCI",
      accessor: (row) => row.totalParticipants,
    },
    {
      key: "completedCount",
      label: "TAMAMLAYAN",
      header: "TAMAMLAYAN",
      accessor: (row) => row.completedCount,
    },
    {
      key: "avgExamScore",
      label: "ORT. SINAV",
      header: "ORT. SINAV",
      accessor: (row) => `${row.avgExamScore.toFixed(1)}`,
    },
    {
      key: "passRate",
      label: t("reports.trainerPerformance.headerPassPct"),
      header: t("reports.trainerPerformance.headerPassPct"),
      accessor: (row) => `%${row.passRate.toFixed(1)}`,
    },
    {
      key: "avgSurveyRating",
      label: "ORT. MEMN.",
      header: "ORT. MEMN.",
      accessor: (row) =>
        row.avgSurveyRating > 0 ? row.avgSurveyRating.toFixed(2) : "—",
    },
    {
      key: "avgSurveyRating2",
      label: "NPS",
      header: "NPS",
      accessor: (row) =>
        row.avgSurveyRating > 0 ? row.npsScore.toFixed(0) : "—",
    },
    {
      key: "avgCompletionDays",
      label: "ORT. TAMAMLAMA",
      header: "ORT. TAMAMLAMA",
      accessor: (row) =>
        row.avgCompletionDays > 0 ? `${row.avgCompletionDays} gün` : "—",
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<InstructorPerformanceItem>({
    reportKey: "INSTRUCTOR_PERFORMANCE",
    reportTitle: t("reports.trainerPerformance.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.trainerPerformance.title")}
      layoutKey={`report:INSTRUCTOR_PERFORMANCE`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.strategic.title")}
      description={t("reports.trainerPerformance.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="INSTRUCTOR_PERFORMANCE"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="egitimci-performans"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          categories={categories}
          showCategory
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDepartment={false}
        />
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <ReportKPICard
          title={t("reports.trainerPerformance.totalTrainer")}
          value={summary?.totalInstructors ?? 0}
          icon={<EmployeeManagement width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.trainerPerformance.totalParticipant")}
          value={summary?.totalParticipants ?? 0}
          icon={<EmployeeManagement width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.trainerPerformance.avgExamScore")}
          value={summary ? summary.avgExamScore.toFixed(1) : "—"}
          icon={<Approve width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title="Ort. Memnuniyet"
          value={summary ? summary.avgSurveyRating.toFixed(2) : "—"}
          icon={<Heart width={18} height={18} />}
          accent="success"
        />
      </div>

      <ReportChart
        title={t("reports.trainerPerformance.top10Trainer")}
        type="bar"
        isLoading={isLoading}
        isEmpty={topByScore.length === 0}
        series={[
          {
            name: t("reports.trainerPerformance.avgExam"),
            data: topByScore.map((i) => i.avgExamScore),
          },
        ]}
        options={{
          xaxis: { categories: topByScore.map((i) => i.fullName) },
          plotOptions: {
            bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
          },
          colors: ["#4f46e5"],
          yaxis: { max: 100 },
        }}
      />

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.instructorID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={error ? t("reports.trainerPerformance.noData") : t("reports.trainerPerformance.noTrainer")}
      />
    </ReportDetailLayout>
  );
};

export default TrainerPerformanceReportPage;
